/* eslint-disable no-console */
import {CloseOutlined} from '@ant-design/icons';
import {Button, Tour} from 'antd';
import React, {useState, useEffect, useMemo} from 'react';

import {drawerZIndex} from '../../../constants/Common';

import {ReactComponent as RightArrowTour} from './../../../assets/icons/RightArrowTour.svg';

interface TakeTourProps {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

const TOUR_STEPS_CONFIG = [
  {
    name: 'menu_item_configuration',
    description: `To set up your organization, you need to enter company data, 
    define boundaries, and configure emission databases. This will
     enable accurate carbon tracking and ESG compliance.`,
    placement: 'right' as const
  },
  {
    name: 'menu_item_product_carbon_accounting',
    description: `Account and track your Scope 1, 2, and 3 emissions to get 
    insights, report, and drive decarbonization.`,
    placement: 'right' as const
  },
  {
    name: 'menu_item_product_esg_reporting',
    description: `Take a step to define your sustainability 
    disclosures by complying with BRSR, GRI, and SASB standards.`,
    placement: 'right' as const
  },
  {
    name: 'menu_item_product_strategy',
    description: `Build a custom Net Zero roadmap with AI-driven insights tailored to your priorities, 
    industry benchmarks, and long-term sustainability goals`,
    placement: 'right' as const
  },
  {
    name: 'menu_item_report_studio',
    description: 'View the reports collection',
    placement: 'right' as const
  },
  {
    name: 'menu_item_activity_log',
    description: `Your trusted platform for tracking and managing 
    your organization’s carbon footprint. We’re here to empower your journey 
    toward sustainability with innovative carbon management solutions.`,
    placement: 'right' as const
  }
];

const TakeTourConfig: React.FC<TakeTourProps> = ({visible, setVisible}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [open, setOpen] = useState(visible);

  useEffect(() => {
    setOpen(visible);
  }, [visible]);

  const steps = useMemo(() => {
    const getTarget = (name: string) => () =>
      document.querySelector(`[name='${name}']`) as HTMLElement;

    return TOUR_STEPS_CONFIG.filter((config) =>
      document.querySelector(`[name="${config.name}"]`)
    ).map((config) => ({
      title: '',
      description: config.description,
      target: getTarget(config.name),
      placement: config.placement
    }));
  }, []);

  const handleClose = () => {
    setVisible(false);
    setOpen(false);
    setCurrentStep(0);
  };

  const handleNext = () => {
    if (currentStep === steps.length - 1) {
      handleClose();
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const renderTourIndicator = (index: number, total: number) => (
    <div className="tour-indicator-container">
      <span className="tour-step-counter">
        {index + 1}/{total}
      </span>

      <div className="tour-controls">
        <a className="tour-skip-btn" onClick={handleClose}>
          Skip
        </a>
        <Button
          shape="circle"
          icon={index === total - 1 ? <CloseOutlined /> : <RightArrowTour />}
          onClick={handleNext}
          className={index === total - 1 ? 'next-button-danger' : 'next-button'}
        />
      </div>
    </div>
  );

  return (
    <div className="tour-wrapper">
      <Tour
        open={open}
        rootClassName="takeTourConfig"
        current={currentStep}
        onChange={setCurrentStep}
        onClose={handleClose}
        steps={steps}
        closeIcon={false}
        indicatorsRender={renderTourIndicator}
        zIndex={drawerZIndex}
      />
    </div>
  );
};

export default TakeTourConfig;
